import React, { useEffect, useState, useRef, memo } from "react";
import { Select, Card, Button, Input, Popconfirm, notification, Tooltip, Form, Row, Col, List, Empty, Tag } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import { IRoleAssignmentDetails, IRoleCapabilityDetails, IRoleDetails, IRolePayload } from "@Models/IRoleDetails";
import SessionManager from "@Core/session";
import { getSystemId, hasCapability, sortObjectByKey } from "@Utils";
import RoleService from "@Services/RoleService";
import { SystemContext, CONTEXT, NoContext } from "@Constants";
import PageHelmet from "@Components/shared/PageHelmet";
import CapabilitiesTable from "@Components/roles/CapabilitiesTable";
import { useDebounce } from "@Utils";
import "@Styles/pages/roleEditorPage.scss";
import MiniSpinner from "@Components/shared/MiniSpinner";
import LargeSpinner from "@Components/shared/LargeSpinner";
import UserService from "@Services/UserService";

const { Option } = Select;
interface IRoleDetailsIndexed extends IRoleDetails {
    index: number;
}

interface ISystemRoleUser {
    roleAssignmentId: string;
    roleId: string;
    userId: string;
    fullName: string;
}

const RoleEditorPage: React.FC = () => {
    const [allRoles, setAllRoles] = useState<IRolePayload>({ roles: [], capabilities: [] });
    const [systemRoleAssignments, setSystemRoleAssignments] = useState<IRoleAssignmentDetails[]>(null);
    const [systemRoleUsers, setSystemRoleUsers] = useState<ISystemRoleUser[]>(null);
    const [currentRole, setCurrentRole] = useState<IRoleDetailsIndexed>({
        roleId: "",
        name: "",
        contextId: 0,
        ownerId: "",
        description: null,
        roleCapabilities: [],
        index: 0,
    });

    // [newRoleId] is for updating the current role state with the newly created role object
    const [newRole, setNewRole] = useState<IRoleDetails>(null);

    /* Updating indicators (states) */
    const [isLoadingRoles, setIsLoadingRoles] = useState<boolean>(false);
    const [duplicating, setDuplicating] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);
    const [updatingRoleName, setUpdatingRoleName] = useState<boolean>(false);
    const [updatingRoleDescription, setUpdatingRoleDescription] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const capabilitiesRef = useRef<IRoleCapabilityDetails[]>();

    const getAllRoles = async () => {
        setIsLoadingRoles(true);
        const service = new RoleService();
        const result = await service.getAll(false);
        if (!result.hasErrors) {
            setAllRoles(result.value);
        }
        setIsLoadingRoles(false);
    };

    const getSystemRoleAssignments = async () => {
        const roleIds = allRoles?.roles?.filter((x) => x.contextId == SystemContext)?.map((x) => x.roleId);
        if (roleIds?.length) {
            const service = new RoleService();
            const result = await service.getUsers(roleIds);
            if (!result.hasErrors) {
                setSystemRoleAssignments(result.value);
            }
        }
    };

    const getRoleUsers = async () => {
        const service = new UserService();
        const uniqueUserIds = [...new Set(systemRoleAssignments?.map((x) => x.userId))];
        const result = await service.getPublic(uniqueUserIds, true);
        if (!result.hasErrors) {
            const updatedSystemRoleUsers: ISystemRoleUser[] =
                systemRoleAssignments
                    ?.filter((assignment) => {
                        const user = result.value.find((x) => x.userId === assignment.userId);
                        return user !== undefined;
                    })
                    ?.map((assignment) => {
                        const user = result.value.find((x) => x.userId === assignment.userId);
                        return {
                            roleAssignmentId: assignment.roleAssignmentId || "",
                            roleId: assignment.roleId,
                            userId: assignment.userId,
                            fullName: user!.fullName,
                        };
                    }) || [];
            setSystemRoleUsers(updatedSystemRoleUsers);
        }
    };

    const getSortedCapabilities = () => {
        return allRoles?.capabilities
            .filter((x) => x.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name));
    };

    const updateRoleName = async () => {
        setUpdatingRoleName(true);
        const service = new RoleService();

        // Update local state.
        const newRoles = { ...allRoles };
        allRoles.roles[prevCountRef.current.index].name = prevCountRef.current.name;
        setAllRoles(newRoles);

        const res = await service.update({ ...prevCountRef.current, name: prevCountRef.current.name.trim() });
        if (!res.hasErrors) {
            notification.success({ message: "Role name updated." });
        } else {
            notification.error({ message: "Error updating role name." });
        }
        setUpdatingRoleName(false);
    };

    const updateRoleDescription = async () => {
        setUpdatingRoleDescription(true);

        // Update local state.
        const newRoles = { ...allRoles };
        allRoles.roles[prevCountRef.current.index].description = prevCountRef.current.description;
        setAllRoles(newRoles);

        const service = new RoleService();
        const res = await service.update({
            ...prevCountRef.current,
            description: prevCountRef.current.description.trim(),
        });
        if (!res.hasErrors) {
            notification.success({ message: "Role description updated." });
        } else {
            notification.error({ message: "Error updating role description." });
        }
        setUpdatingRoleDescription(false);
    };

    const [debounceRoleNameInput] = useDebounce();
    const onRoleNameInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = e;
        setCurrentRole((prevState: IRoleDetailsIndexed) => {
            return {
                ...prevState,
                name: value,
            };
        });
        debounceRoleNameInput(updateRoleName, 2000);
    };

    const [debounceRoleDescriptionInput] = useDebounce();
    const onRoleDescriptionInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = e;
        setCurrentRole((prevState: IRoleDetailsIndexed) => {
            return {
                ...prevState,
                description: value,
            };
        });
        debounceRoleDescriptionInput(updateRoleDescription, 2000);
    };

    /**
     * Check if user has the named system role provided.
     * @param cap
     */
    const hasSystemCap = (cap: string) => {
        return hasCapability(cap, SystemContext, getSystemId());
    };

    /**
     * Handle changing the selected role.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onRoleSelectChange = async (value: any) => {
        const optionIndex = allRoles?.roles.findIndex((item) => item.roleId === value);
        if (optionIndex === -1) return;
        const newCurrent: IRoleDetailsIndexed = { ...allRoles?.roles[optionIndex], index: optionIndex };
        setCurrentRole(newCurrent);
        setNewRole(null);
        capabilitiesRef.current = currentRole.roleCapabilities;
    };

    /**
     * Update a single capability context. This triggers a full role update.
     * @param capabilityId
     * @param value
     */
    const handleCapabilityChange = async (capabilityId: string, value: number) => {
        const index = currentRole?.roleCapabilities?.findIndex(
            (capability) => capability?.capabilityId === capabilityId
        );
        if (index !== -1) {
            const updatedRole = { ...currentRole };
            updatedRole.roleCapabilities[index] = { capabilityId: capabilityId, contextId: value };
            setCurrentRole(updatedRole);
            const service = new RoleService();
            const res = await service.update(updatedRole);
            if (!res.hasErrors) {
                notification.success({ message: "Capability updated." });
            } else {
                notification.error({ message: "Error updating capability." });
            }
        }
    };

    /**
     * Duplicate an existing role, add it to role options and select it.
     */
    const handleDuplicate = async () => {
        setDuplicating(true);
        const service = new RoleService();
        const res = await service.copy(SessionManager.getServiceUser()?.userId, currentRole.roleId);
        setDuplicating(false);
        if (!res.hasErrors) {
            const duplicatedRole = { ...newRole, roleId: res.value.id };
            const newRoles = { ...allRoles };
            newRoles.roles.push(duplicatedRole);
            setAllRoles(newRoles);
            setNewRole(duplicatedRole);
            notification.success({ message: "Role duplicated." });
        } else {
            notification.error({ message: "Error duplicating role." });
        }
    };

    /**
     * Delete a role and all assignments.
     */
    const handleDeleteConfirm = async () => {
        const deleteId = currentRole.roleId;
        const newCurrent: IRoleDetailsIndexed = { ...allRoles?.roles[0], index: 0 };
        setCurrentRole(newCurrent);
        setDeleting(true);
        const service = new RoleService();
        const res = await service.delete(deleteId);
        if (!res.hasErrors) {
            const newRoles: IRolePayload = {
                capabilities: allRoles.capabilities,
                roles: allRoles.roles.filter((x) => x.roleId !== deleteId),
            };
            setAllRoles(newRoles);
            notification.success({ message: "Role deleted." });
        } else {
            notification.error({ message: "Error deleting role." });
        }
        setDeleting(false);
    };

    const onCapabilitiesSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const {
            target: { value },
        } = e;
        setSearchTerm(value);
    };

    const removeAction = async (roleAssignmentId: string, userId: string, roleId: string) => {
        const service = new UserService();
        const res = await service.removeRoles([userId], roleId, getSystemId());
        if (!res.hasErrors) {
            notification.success({ message: "Role removed." });
            setSystemRoleAssignments([...systemRoleAssignments.filter((x) => x.roleAssignmentId != roleAssignmentId)]);
        } else {
            notification.error({ message: "Error removing role." });
        }
    };

    useEffect(() => {
        getAllRoles().then();
    }, []);

    // Create ref for previous "role" state before api requests
    const prevCountRef = useRef<IRoleDetailsIndexed>(null);
    useEffect(() => {
        prevCountRef.current = currentRole;
    }, [currentRole]);

    useEffect(() => {
        if (allRoles?.roles.length && allRoles?.capabilities.length) {
            let currentIndex = 0;

            // Check for previously selected role
            if (prevCountRef.current?.index) {
                const role = allRoles?.roles.find((item) => item.roleId === prevCountRef.current.roleId);
                const roleIndex = role?.index;

                if (role == null || !roleIndex) {
                    // Role not found
                    currentIndex = prevCountRef.current?.index;
                    setCurrentRole({ ...role, index: currentIndex });
                } else if (allRoles?.roles.length > 1) {
                    // Role found, use its index
                    setCurrentRole({ ...allRoles?.roles[currentIndex], index: roleIndex });
                }
            } else {
                if (allRoles?.roles.length > 1) {
                    currentIndex = 1;
                    setCurrentRole({ ...allRoles?.roles[currentIndex], index: currentIndex });
                }
            }

            //  Save capabilities to the capabilitiesRef for search/filter actions
            capabilitiesRef.current = allRoles.roles[currentIndex].roleCapabilities;
        }
        if (allRoles?.roles?.length) getSystemRoleAssignments().then();
    }, [allRoles]);

    useEffect(() => {
        // Update [role] state if New Role object had just been created
        if (newRole) {
            setCurrentRole({ ...newRole, index: allRoles.roles.length });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newRole]);

    useEffect(() => {
        getRoleUsers().then();
    }, [systemRoleAssignments]);

    const isSystemAdminRole = +currentRole?.index === NoContext;
    const hasCapabilityCreate = hasSystemCap("Role/Copy");
    const hasCapabilityDelete = hasSystemCap("Role/Delete");
    const hasCapabilityUpdate = hasSystemCap("Role/Update");

    return (
        <article className="roles-page">
            <PageHelmet name={"Roles"} />
            <section>
                {isLoadingRoles ? (
                    <LargeSpinner height={600} />
                ) : (
                    <div>
                        <Card style={{ width: "100%" }} className={"newUI-subtle"}>
                            <Row>
                                <Col span={12}>
                                    <Form>
                                        <Form.Item className="roles-editor-form-item" label={"Role"}>
                                            <Select
                                                placeholder="Select role"
                                                value={currentRole?.name}
                                                className="select"
                                                onChange={onRoleSelectChange}
                                                loading={isLoadingRoles}
                                            >
                                                {allRoles?.roles.map((item, key) => (
                                                    <Option key={key} value={item.roleId}>
                                                        {item.name}
                                                    </Option>
                                                ))}
                                            </Select>
                                            <Tooltip
                                                title={
                                                    !hasCapabilityCreate || isSystemAdminRole
                                                        ? "You cannot duplicate this role"
                                                        : "Create a new role based on this one"
                                                }
                                            >
                                                <Button
                                                    style={{ margin: "10px 10px 0px 0px" }}
                                                    className="secondary"
                                                    loading={duplicating}
                                                    onClick={handleDuplicate}
                                                    disabled={!hasCapabilityCreate || isSystemAdminRole}
                                                >
                                                    Duplicate
                                                </Button>
                                            </Tooltip>
                                            <Popconfirm
                                                overlayStyle={{ maxWidth: "300px" }}
                                                title="This will also delete all user assignments of this role. It cannot be undone. Are you sure?"
                                                onConfirm={handleDeleteConfirm}
                                                okText="Delete"
                                                cancelText="Cancel"
                                                cancelButtonProps={{ className: "secondary" }}
                                                disabled={!hasCapabilityDelete || isSystemAdminRole}
                                            >
                                                <Button
                                                    style={{ margin: "10px 0px 0px 0px" }}
                                                    className="secondary danger"
                                                    disabled={!hasCapabilityDelete || isSystemAdminRole}
                                                    loading={deleting}
                                                >
                                                    Delete
                                                </Button>
                                            </Popconfirm>
                                        </Form.Item>
                                        <Form.Item
                                            className="roles-editor-form-item"
                                            label={<> {updatingRoleName && <MiniSpinner />} Name</>}
                                        >
                                            <Input
                                                placeholder="Name"
                                                value={currentRole?.name}
                                                onChange={onRoleNameInputChange}
                                                disabled={isSystemAdminRole || !hasCapabilityUpdate}
                                            />
                                        </Form.Item>
                                        <Form.Item
                                            className="roles-editor-form-item"
                                            label={<> {updatingRoleDescription && <MiniSpinner />} Description</>}
                                        >
                                            <Input
                                                placeholder="Description"
                                                value={currentRole?.description}
                                                onChange={onRoleDescriptionInputChange}
                                                disabled={isSystemAdminRole || !hasCapabilityUpdate}
                                            />
                                        </Form.Item>
                                        <Form.Item className="roles-editor-form-item" label="Context">
                                            <span>
                                                This role must be assigned in a{" "}
                                                <Tag color={"cyan"}>
                                                    {Object.values(CONTEXT)[currentRole?.contextId ?? 0]}
                                                </Tag>
                                                context.
                                            </span>
                                        </Form.Item>
                                    </Form>
                                </Col>
                                <Col span={12} style={{ paddingLeft: "20px" }}>
                                    {systemRoleUsers?.length > 0 && currentRole.contextId === SystemContext && (
                                        <div>
                                            <h4>{"Users with " + currentRole.name + " role"}</h4>
                                            <div className={"role-user-list"}>
                                                <List
                                                    locale={{
                                                        emptyText: (
                                                            <Empty
                                                                style={{ paddingBottom: "40px" }}
                                                                description={"No users."}
                                                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                                            />
                                                        ),
                                                    }}
                                                    dataSource={sortObjectByKey(systemRoleUsers, "fullName").filter(
                                                        (x) => x.roleId === currentRole.roleId
                                                    )}
                                                    itemLayout="vertical"
                                                    renderItem={(item, i) => (
                                                        <List.Item
                                                            className={
                                                                i % 2
                                                                    ? "list-item list-item-odd"
                                                                    : "list-item list-item-even"
                                                            }
                                                        >
                                                            <table>
                                                                <tbody>
                                                                    <tr>
                                                                        <td className="role-users-list-user-item-name">
                                                                            {item.fullName}
                                                                        </td>
                                                                        <td className="role-users-list-close-icon">
                                                                            <Tooltip title="Remove role">
                                                                                <Popconfirm
                                                                                    title={"Remove role?"}
                                                                                    onConfirm={() =>
                                                                                        removeAction(
                                                                                            item.roleAssignmentId,
                                                                                            item.userId,
                                                                                            item.roleId
                                                                                        )
                                                                                    }
                                                                                    okText="Yes"
                                                                                    cancelText="No"
                                                                                    cancelButtonProps={{
                                                                                        className: "secondary",
                                                                                    }}
                                                                                >
                                                                                    <button className={"no-style"}>
                                                                                        <CloseCircleFilled />
                                                                                    </button>
                                                                                </Popconfirm>
                                                                            </Tooltip>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </List.Item>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Card>
                        <div style={{ height: "20px", display: "block" }}></div>
                        <CapabilitiesTable
                            handleCapabilitySearch={onCapabilitiesSearchChange}
                            capabilitySearch={searchTerm}
                            handleCapabilityChange={handleCapabilityChange}
                            isSystemAdminRole={isSystemAdminRole}
                            hasCapabilityUpdate={hasCapabilityUpdate}
                            role={currentRole}
                            capabilities={getSortedCapabilities()}
                        />
                    </div>
                )}
            </section>
        </article>
    );
};

export default memo(RoleEditorPage);
